'use client';
import Image from 'next/image';
import { motion, useInView } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { useRef } from 'react';
import { useRouter } from 'next/navigation';

import { Button } from '@/slw/shared/ui/button';
import { AnimatedText } from '@/slw/shared/ui/animated-text';
import { useCheckUserHasSlotQuery } from '@/slw/entities/about-us';
import { useAuthStore, useGlobalStore } from '@/slw/shared/stores';

export function FooterBanner() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const { user } = useAuthStore();
  const { openAuthModal } = useGlobalStore();

  const { data } = useCheckUserHasSlotQuery(!!user?.id);
  const router = useRouter();
  const handleClick = () => {
    if (user?.id) {
      if (!data?.id) {
        router.push('/sell-your-car');
      } else {
        router.push(`/seller/listing?package=${data.id}`);
      }
    } else {
      openAuthModal();
    }
  };

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8 }}
      className='
        relative
        w-full
        max-w-full
        mx-auto
        h-auto
        py-10
        flex items-center justify-center
        rounded-[2rem]
        overflow-hidden
      '
    >
      {/* Ảnh nền với filter sepia và animation */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 1, ease: 'easeOut' }}
        className='absolute inset-0 w-full h-full'
      >
        <Image
          src='/images/about-us/about-banner-car2.png'
          alt='About Us Banner Car'
          fill
          className='object-cover object-center filter sepia-[0.5] grayscale-[0.3]'
          priority
          quality={90}
          sizes='(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 1360px'
        />
        {/* Overlay màu nâu */}
        <div className='absolute inset-0 z-10' />
      </motion.div>
      {/* Content với animation */}
      <div className='relative z-20 flex flex-col items-end justify-between w-full h-full pr-12 py-8 xl:pr-28 lg:pr-20'>
        <AnimatedText
          isInView={isInView}
          text="Share your Car's Story With"
          className='text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl 
          xl:text-[4rem] font-bold drop-shadow-lg text-center md:text-left flex-1'
        />
        <AnimatedText
          isInView={isInView}
          delayChildren={0.8}
          text='the Community!'
          className='text-white text-2xl mb-[1.375rem] sm:text-3xl 
          md:text-4xl lg:text-5xl xl:text-[4rem] font-bold drop-shadow-lg text-center md:text-left flex-1'
        />
        <motion.div
          variants={{
            visible: { opacity: 1, y: 0 },
            hidden: { opacity: 0, y: 40 },
          }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.24 }}
        >
          <Button onClick={handleClick} variant='default' color='primary' className='font-bold cursor-pointer'>
            LIST IT NOW!
            <PlusIcon className='w-4 h-4' />
          </Button>
        </motion.div>
      </div>
    </motion.section>
  );
}
